/* ============================================================================
   Site photos — each photo syncs to D1 as its own row (kind "photo") so a
   growing gallery never breaches per-aggregate size limits.

   MEMORY DISCIPLINE. The studio now carries ~1,900 photographs (≈340 MB of
   thumbnails as base64) across a dozen projects. Holding all of that in one
   in-memory array — and cloning it whole into IndexedDB on every commit — is
   what was killing Mobile Safari. So:

     • every row lives in IndexedDB as its OWN record, indexed by project;
     • memory holds only the rows of projects a screen has actually asked for
       (usePhotos(projectId) loads that project; nothing else is resident);
     • a light META index (id, project, date, bytes) covers counts and sizes
       for every project without touching a single thumbnail;
     • a commit persists just the rows it changed.

   Seeded placeholders are lightweight generative SVGs standing in for real
   site photography until uploads replace them.
   ========================================================================== */
import { useEffect, useState } from "react";
import { uid } from "./store";
import { videoPoster, VIDEO_MAX_BYTES, encodePhoto } from "./image";
import { uploadFile, uploadFileProgress, fileUrl } from "./files";
import type { SitePhoto } from "./types";

const LEGACY_KEY = "atelier.photos.v1";   // the pre-IndexedDB localStorage slot

/** The light index entry — enough for counts, sizes and sync bookkeeping. */
export interface PhotoMeta {
  id: string;
  projectId: string;
  date: string;
  bytes: number;             // thumbnail size on this device; 0 = row not stored here
  category?: SitePhoto["category"];
  media?: SitePhoto["media"];
}

/* ------------------------------------------------ seeded "photography" -- */
function svgPhoto(variant: number, hue: number, label: string): string {
  const sky = `hsl(${30 + variant * 8} 60% 78%)`;
  const sky2 = `hsl(${hue} 30% 62%)`;
  const ground = `hsl(${hue} 22% 42%)`;
  const wall = `hsl(${hue} 18% 55%)`;
  const scaff = Array.from({ length: 5 }, (_, i) => {
    const x = 60 + i * 110 + (variant * 17) % 40;
    return `<line x1='${x}' y1='120' x2='${x}' y2='330' stroke='rgba(40,32,24,.55)' stroke-width='4'/><line x1='${x - 30}' y1='${170 + i * 10}' x2='${x + 80}' y2='${160 + i * 10}' stroke='rgba(40,32,24,.4)' stroke-width='3'/>`;
  }).join("");
  const blocks = Array.from({ length: 8 }, (_, i) => {
    const bx = 40 + ((i * 73 + variant * 31) % 480);
    const bh = 40 + ((i * 37) % 90);
    return `<rect x='${bx}' y='${330 - bh}' width='${60 + (i % 3) * 22}' height='${bh}' fill='${wall}' opacity='${0.55 + (i % 4) * 0.1}'/>`;
  }).join("");
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 640 420'>
<defs><linearGradient id='sk' x1='0' y1='0' x2='0' y2='1'><stop offset='0' stop-color='${sky}'/><stop offset='1' stop-color='${sky2}'/></linearGradient></defs>
<rect width='640' height='420' fill='url(#sk)'/>
<circle cx='${520 - variant * 30}' cy='70' r='34' fill='rgba(255,246,220,.85)'/>
<path d='M${80 + variant * 40} 40 v130 h180 M${80 + variant * 40} 55 l150 -12' stroke='rgba(40,32,24,.5)' stroke-width='5' fill='none'/>
${blocks}${scaff}
<rect y='330' width='640' height='90' fill='${ground}'/>
<rect y='330' width='640' height='8' fill='rgba(0,0,0,.18)'/>
<rect x='14' y='368' width='${label.length * 9 + 26}' height='30' rx='4' fill='rgba(20,16,10,.65)'/>
<text x='27' y='389' font-family='monospace' font-size='15' fill='#f6f2e8'>${label}</text>
</svg>`;
  return "data:image/svg+xml;utf8," + encodeURIComponent(svg);
}

function iso(d: string) { return new Date(d).toISOString(); }

function seedPhotos(): SitePhoto[] {
  const mk = (n: number, date: string, activity: string, caption: string, by = "Cornerstone Civil Ltd."): SitePhoto => ({
    id: `ph-seed-${n}`, projectId: "prj-potaro", date, activity, caption, by,
    takenAt: iso(date + "T15:30"), dataUri: svgPhoto(n, 90 + n * 24, `PG-2 · ${date}`),
  });
  return [
    mk(1, "2026-07-13", "Blockwork", "First-floor blockwork, units 7–9 east elevation"),
    mk(2, "2026-07-13", "Drainage", "Trench open 0+080 to 0+140, bedding placed"),
    mk(3, "2026-07-10", "Concrete", "Ground slab pour, unit 11 — finishing pass"),
    mk(4, "2026-07-10", "Roofing", "Anchorage brackets set out, units 1–4"),
    mk(5, "2026-07-09", "Foundations", "Pad F3 excavation at grid D2 — obstruction visible"),
    mk(6, "2026-07-08", "Site overview", "Compound and block A from the water tower", "R. Ferdinand"),
  ];
}

/* ---------------------------------------------------------------- state -- */
const rows = new Map<string, SitePhoto>();      // resident full rows — loaded projects only
const meta = new Map<string, PhotoMeta>();      // every photo known on this device (light)
const loaded = new Set<string>();               // projectIds whose rows are resident
const loading = new Map<string, Promise<void>>();

/* A phone keeps a bounded slice of a project resident — the NEWEST rows —
   and reaches for older ones by id when a report or the gallery asks. A
   desktop still holds the whole project. STUDIO 9 alone is 170 MB of
   thumbnails; Safari will not hold that, and nobody opening today's report
   needs March. */
const MOBILE = typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(max-width: 860px)").matches;
/* 60, not 200: the project menu holds this slice on EVERY page, and rows that
   still embed their thumbnail run to ~780 KB each — 200 of them under the
   Drawings register was enough for iOS to kill the tab. The gallery pages
   older photos in as you scroll. */
const RESIDENT_LIMIT = MOBILE ? 60 : Infinity;
const MORE_STEP = MOBILE ? 60 : 150;
const SINGLES_MAX = MOBILE ? 60 : 600;
/** Photo ids this device has asked the server for by name — a report's
    photos that were never downloaded, older gallery pages. */
const requested = new Set<string>();
export function requestedPhotoIds(): string[] { return [...requested]; }
export function isMobilePhotoMode(): boolean { return MOBILE; }
let photos: SitePhoto[] = [];                   // cached array view of `rows`
const subs = new Set<() => void>();

const metaOf = (p: SitePhoto): PhotoMeta => ({
  id: p.id, projectId: p.projectId, date: p.date, bytes: p.dataUri?.length ?? 0, category: p.category, media: p.media,
});
function rebuild() { photos = [...rows.values()]; }
function emit() { subs.forEach((f) => f()); }

/* --------------------------------------------------------------- IDB ----- */
const IDB_NAME = "atelier-uploads";
const IDB_STORE = "originals";               // the resumable original-upload queue
const IDB_LEGACY_PHOTOS = "photos";          // v2: one giant {id:"state", photos:[…]} record
const IDB_ROWS = "photoRows";                // v3: one record per photo, indexed by project
const IDB_META = "photoMeta";                // v3: the light index
const MAX_RESUME_ATTEMPTS = 5;

/* One connection for the life of the page — every call used to open another,
   and a report page with dozens of photos held dozens open. */
let idbCached: Promise<IDBDatabase | null> | null = null;
function idb(): Promise<IDBDatabase | null> {
  if (!idbCached) idbCached = openIdb().then((db) => {
    if (!db) idbCached = null;
    else { db.onversionchange = () => { db.close(); idbCached = null; }; db.onclose = () => { idbCached = null; }; }
    return db;
  });
  return idbCached;
}
function openIdb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(IDB_NAME, 3);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(IDB_STORE)) db.createObjectStore(IDB_STORE, { keyPath: "id" });
        if (!db.objectStoreNames.contains(IDB_LEGACY_PHOTOS)) db.createObjectStore(IDB_LEGACY_PHOTOS, { keyPath: "id" });
        if (!db.objectStoreNames.contains(IDB_ROWS)) {
          const s = db.createObjectStore(IDB_ROWS, { keyPath: "id" });
          s.createIndex("projectId", "projectId", { unique: false });
        }
        if (!db.objectStoreNames.contains(IDB_META)) db.createObjectStore(IDB_META, { keyPath: "id" });
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch { resolve(null); }
  });
}

/** Run one transaction over the given stores; resolves when it commits. */
function tx(db: IDBDatabase, stores: string[], mode: IDBTransactionMode, fn: (t: IDBTransaction) => void): Promise<boolean> {
  return new Promise((resolve) => {
    try {
      const t = db.transaction(stores, mode);
      t.oncomplete = () => resolve(true);
      t.onerror = () => resolve(false);
      t.onabort = () => resolve(false);
      fn(t);
    } catch { resolve(false); }
  });
}
function getAll<T>(db: IDBDatabase, store: string, index?: string, key?: IDBValidKey): Promise<T[]> {
  return new Promise((resolve) => {
    try {
      const os = db.transaction(store, "readonly").objectStore(store);
      const src = index ? os.index(index) : os;
      const req = key === undefined ? src.getAll() : src.getAll(key);
      req.onsuccess = () => resolve((req.result ?? []) as T[]);
      req.onerror = () => resolve([]);
    } catch { resolve([]); }
  });
}
function getOne<T>(db: IDBDatabase, store: string, key: IDBValidKey): Promise<T | null> {
  return new Promise((resolve) => {
    try {
      const req = db.transaction(store, "readonly").objectStore(store).get(key);
      req.onsuccess = () => resolve((req.result as T) ?? null);
      req.onerror = () => resolve(null);
    } catch { resolve(null); }
  });
}

/** Persist just these rows (and their meta). Chunked so a big merge never
    holds one enormous transaction open on a phone. */
async function persistRows(list: SitePhoto[]): Promise<boolean> {
  if (!list.length) return true;
  const db = await idb();
  if (!db) return false;
  let ok = true;
  for (let i = 0; i < list.length; i += 40) {
    const chunk = list.slice(i, i + 40);
    ok = (await tx(db, [IDB_ROWS, IDB_META], "readwrite", (t) => {
      const r = t.objectStore(IDB_ROWS), m = t.objectStore(IDB_META);
      for (const p of chunk) { r.put(p); m.put(metaOf(p)); }
    })) && ok;
  }
  return ok;
}
async function deleteRow(id: string) {
  const db = await idb();
  if (!db) return;
  await tx(db, [IDB_ROWS, IDB_META], "readwrite", (t) => { t.objectStore(IDB_ROWS).delete(id); t.objectStore(IDB_META).delete(id); });
}

/* ------------------------------------------------------------ hydration -- */
/* Boot loads ONLY the light index. Rows come in per project, on demand. The
   first boot after the old single-record era splits that record into rows —
   and the pre-IndexedDB localStorage slot before it — then clears them. */
export const photoStoreReady: Promise<void> = (typeof indexedDB === "undefined" ? Promise.resolve() : (async () => {
  const db = await idb();
  if (!db) return;

  // one-time migrations, oldest first
  let legacy: SitePhoto[] | null = null;
  try {
    const raw = localStorage.getItem(LEGACY_KEY);
    if (raw) legacy = (JSON.parse(raw) as { photos?: SitePhoto[] }).photos ?? null;
  } catch { /* unreadable — ignore */ }
  const v2 = await getOne<{ id: string; photos: SitePhoto[] }>(db, IDB_LEGACY_PHOTOS, "state");
  const toSplit = [...(legacy ?? []), ...(v2?.photos ?? [])];
  if (toSplit.length) {
    const seen = new Set<string>();
    const unique = toSplit.filter((p) => (seen.has(p.id) ? false : (seen.add(p.id), true)));
    if (await persistRows(unique)) {
      await tx(db, [IDB_LEGACY_PHOTOS], "readwrite", (t) => t.objectStore(IDB_LEGACY_PHOTOS).delete("state"));
      try { localStorage.removeItem(LEGACY_KEY); } catch { /* nothing to free */ }
    }
  }

  for (const m of await getAll<PhotoMeta>(db, IDB_META)) meta.set(m.id, m);

  // nothing on this device at all → the demo placeholders, so the seeded
  // project still reads as a project
  if (meta.size === 0) {
    const seeds = seedPhotos();
    for (const p of seeds) { rows.set(p.id, p); meta.set(p.id, metaOf(p)); }
    loaded.add("prj-potaro");
    await persistRows(seeds);
  }
  rebuild();
  emit();
})());

/** Bring one project's rows into memory. Idempotent; concurrent calls share
    the same load. Tells the sync layer so rows the server has but this device
    doesn't are fetched now rather than on the next poll. */
export function loadProject(projectId: string): Promise<void> {
  if (!projectId || loaded.has(projectId)) return Promise.resolve();
  const inFlight = loading.get(projectId);
  if (inFlight) return inFlight;
  const job = (async () => {
    await photoStoreReady;
    const db = await idb();
    if (db) {
      if (RESIDENT_LIMIT === Infinity) {
        for (const p of await getAll<SitePhoto>(db, IDB_ROWS, "projectId", projectId)) {
          if (!rows.has(p.id)) rows.set(p.id, p);
          if (!meta.has(p.id)) meta.set(p.id, metaOf(p));
        }
      } else {
        await residentSlice(db, projectId, RESIDENT_LIMIT);   // newest first, bounded
      }
    }
    loaded.add(projectId);
    loading.delete(projectId);
    rebuild();
    emit();
    demandListener?.();
  })();
  loading.set(projectId, job);
  return job;
}

/** Bring the next `want` newest rows of a project that are on disk but not
    yet in memory. Returns how many came in. */
async function residentSlice(db: IDBDatabase, projectId: string, want: number): Promise<number> {
  const order = [...meta.values()]
    .filter((m) => m.projectId === projectId && m.bytes > 0 && !rows.has(m.id))
    .sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  let n = 0;
  for (const m of order) {
    if (n >= want) break;
    const r = await getOne<SitePhoto>(db, IDB_ROWS, m.id);
    if (r) { rows.set(r.id, r); n++; }
  }
  return n;
}

/** The gallery's "show older": another slice into memory from disk, and the
    rows this device never downloaded asked for from the server — newest of
    the missing first. */
export async function loadMorePhotos(projectId: string, step = MORE_STEP): Promise<void> {
  await loadProject(projectId);
  const db = await idb();
  if (db) await residentSlice(db, projectId, step);
  const missing = [...meta.values()]
    .filter((m) => m.projectId === projectId && m.bytes === 0 && !requested.has(m.id))
    .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
    .slice(0, step);
  for (const m of missing) requested.add(m.id);
  rebuild();
  emit();
  if (missing.length) demandListener?.();
}

/** How much of a project this device holds vs knows about — drives the
    gallery's "show older" control. */
export function photoCounts(projectId: string): { known: number; resident: number; onDisk: number } {
  let known = 0, resident = 0, onDisk = 0;
  for (const m of meta.values()) {
    if (m.projectId !== projectId) continue;
    known++;
    if (m.bytes > 0) onDisk++;
    if (rows.has(m.id)) resident++;
  }
  return { known, resident, onDisk };
}

/** Projects whose rows this device currently wants resident — what the sync
    layer fetches full rows for. Everything else is bookkept as meta only. */
export function demandedProjects(): Set<string> { return new Set([...loaded, ...loading.keys()]); }
let demandListener: (() => void) | null = null;
export function onPhotoDemand(f: () => void) { demandListener = f; }

/* ------------------------------------------------------------- reading --- */
export function usePhotos(projectId?: string): SitePhoto[] {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);
  useEffect(() => { if (projectId) void loadProject(projectId); }, [projectId]);
  return projectId ? photos.filter((p) => p.projectId === projectId) : photos;
}
/** The light index — every photo known on this device, no thumbnails. Use it
    for counts and sizes across projects; it never costs a megabyte. */
export function usePhotoMeta(): PhotoMeta[] {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);
  return [...meta.values()];
}
/** One row by id — for a cover image on a list page, where loading the whole
    project's gallery would be the memory problem all over again. Fetched from
    disk on demand and cached; resident rows are served straight from memory. */
const singles = new Map<string, SitePhoto | null>();   // null = load in progress / not here
function rememberSingle(p: SitePhoto) {
  singles.delete(p.id);
  singles.set(p.id, p);
  while (singles.size > SINGLES_MAX) singles.delete(singles.keys().next().value as string);
}
/** One row by id: disk first, then the server by name. Resolves null when
    neither has it yet — the server copy arrives through the sync poll and
    lands in the cache, and every subscriber re-renders. */
async function loadSingle(id: string): Promise<SitePhoto | null> {
  singles.set(id, null);
  await photoStoreReady;
  const db = await idb();
  const r = db ? await getOne<SitePhoto>(db, IDB_ROWS, id) : null;
  if (r) { rememberSingle(r); emit(); return r; }
  requested.add(id);
  demandListener?.();
  return null;
}
export function usePhotoRow(id?: string): SitePhoto | undefined {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);
  useEffect(() => {
    if (id && !rows.has(id) && !singles.has(id)) void loadSingle(id);
  }, [id]);
  return id ? (rows.get(id) ?? singles.get(id) ?? undefined) : undefined;
}
/** Several rows by id, for a report's photo strip — subscribes, and starts
    loading anything not resident, so a phone shows a report's photos without
    holding the project's whole history. */
export function usePhotoRows(ids: string[] | undefined): SitePhoto[] {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);
  return (ids ?? []).map(photoById).filter((p): p is SitePhoto => !!p);
}
/** Make rows available before something synchronous needs them (a PDF).
    Loads from disk, asks the server for the rest and waits a bounded time
    for them to arrive. */
export async function ensurePhotoRows(ids: string[], waitMs = 12_000): Promise<void> {
  await photoStoreReady;
  const db = await idb();
  const missing: string[] = [];
  for (const id of ids) {
    if (rows.has(id) || singles.get(id)) continue;
    const r = db ? await getOne<SitePhoto>(db, IDB_ROWS, id) : null;
    if (r) rememberSingle(r); else missing.push(id);
  }
  if (!missing.length) return;
  for (const id of missing) requested.add(id);
  demandListener?.();
  const t0 = Date.now();
  while (Date.now() - t0 < waitMs && missing.some((id) => !rows.has(id) && !singles.get(id))) {
    await new Promise((r) => setTimeout(r, 400));
  }
}
export function getPhotos() { return photos; }
/** Resident or cached; otherwise starts a background load and answers
    undefined until it lands (subscribers re-render when it does). */
export function photoById(id: string | undefined): SitePhoto | undefined {
  if (!id) return undefined;
  const r = rows.get(id) ?? singles.get(id);
  if (r) return r;
  if (!singles.has(id)) void loadSingle(id);
  return undefined;
}
export function hasPhotoLocally(id: string): boolean { return (meta.get(id)?.bytes ?? 0) > 0; }

/* --- sync hooks --------------------------------------------------------- */
let photoListener: ((p: SitePhoto) => void) | null = null;
export function onPhotoChange(f: (p: SitePhoto) => void) { photoListener = f; }
let photoDeleteListener: ((id: string) => void) | null = null;
export function onPhotoDelete(f: (id: string) => void) { photoDeleteListener = f; }
export function getPhotoAggregates(): SitePhoto[] { return photos; }

/** Server rows arriving: persisted per record, indexed, and made resident only
    when their project is one this device has asked for. */
export function replacePhotoAggregates(incoming: SitePhoto[]) {
  const residentByProject = new Map<string, number>();
  if (RESIDENT_LIMIT !== Infinity) for (const r of rows.values()) residentByProject.set(r.projectId, (residentByProject.get(r.projectId) ?? 0) + 1);
  for (const p of incoming) {
    meta.set(p.id, metaOf(p));
    const asked = requested.delete(p.id);
    if (asked || singles.has(p.id)) rememberSingle(p);
    const inUse = loaded.has(p.projectId) || loading.has(p.projectId);
    if (!inUse) continue;
    // a phone's resident slice stays bounded; anything asked for by name always lands
    const n = residentByProject.get(p.projectId) ?? 0;
    if (asked || rows.has(p.id) || n < RESIDENT_LIMIT) {
      if (!rows.has(p.id)) residentByProject.set(p.projectId, n + 1);
      rows.set(p.id, p);
    }
  }
  void persistRows(incoming);
  rebuild();
  emit();
}
/** Stamps for photos the server holds but this device hasn't fetched — kept
    as meta (project + id + when) so the project list still counts them and
    "show older" knows which to ask for first. */
export function notePhotoStamps(list: { id: string; pid?: string; updated_at?: number }[]) {
  let changed = false;
  for (const s of list) {
    if (!s.pid || meta.has(s.id)) continue;
    const date = typeof s.updated_at === "number" && isFinite(s.updated_at) ? new Date(s.updated_at).toISOString().slice(0, 10) : "";
    meta.set(s.id, { id: s.id, projectId: s.pid, date, bytes: 0 });
    changed = true;
  }
  if (changed) emit();
}

/** Re-offer to the sync layer any photo the server has never stored — the
    backlog left when an oversized row wedged the old outbox, plus any future
    gap. Rows not resident are read from disk; oversized thumbnails are shrunk
    first. Seeds are skipped — they were only ever local scaffolding. */
export async function resyncMissingPhotos(serverIds: Set<string>, deletedIds?: Set<string>) {
  const missing = [...meta.values()].filter((m) => m.bytes > 0 && !serverIds.has(m.id) && !deletedIds?.has(m.id) && !m.id.startsWith("ph-seed-"));
  if (!missing.length) return;
  const db = await idb();
  for (const m of missing) {
    const row = rows.get(m.id) ?? (db ? await getOne<SitePhoto>(db, IDB_ROWS, m.id) : null);
    if (!row) continue;
    if ((row.dataUri?.length ?? 0) > SAFE_THUMB_BYTES && /^data:image\//i.test(row.dataUri)) {
      const fit = await shrinkToFit(row.dataUri);
      if (!fit) continue;
      const updated = { ...row, dataUri: fit };
      commit([updated]);
      photoListener?.(updated);
    } else {
      photoListener?.(row);
    }
  }
}

/** A photo deleted on ANOTHER device: drop this device's copy quietly —
    no delete is pushed back (the server already knows) and nothing can
    re-upload it, because the caller also tombstones the id. */
export function dropPhotosLocally(ids: string[]) {
  const have = ids.filter((id) => rows.has(id) || meta.has(id));
  for (const id of ids) singles.delete(id);
  if (have.length) commit([], have);
}

/** The best available source for a photograph, and the light one for grids. */
export function photoSrc(p: SitePhoto, want: "full" | "thumb" = "thumb"): string {
  if (want === "full" && p.fileId && p.media !== "video") return fileUrl(p.fileId, p.caption);
  return p.dataUri;
}
export function hasFullCopy(p: SitePhoto): boolean {
  return !!p.fileId && p.media !== "video" && !!p.fullWidth;
}

/* ---------------------------------------------------------------- commit -- */
/** Apply changed rows (and removals) to memory and disk — only what changed. */
function commit(changed: SitePhoto[], removed: string[] = []) {
  for (const p of changed) { rows.set(p.id, p); meta.set(p.id, metaOf(p)); }
  for (const id of removed) { rows.delete(id); meta.delete(id); void deleteRow(id); }
  if (changed.length) void persistRows(changed);
  rebuild();
  emit();
}

/* The server stores a photo row as one JSON aggregate capped at 900 KB, so the
   thumbnail must stay well under that — a 2560px panorama thumbnail can exceed
   it, and an oversized row is rejected on sync. Anything past the cap is shrunk
   in the background and re-committed; the full-quality original in R2 is
   untouched. */
const SAFE_THUMB_BYTES = 780_000;
async function shrinkToFit(dataUri: string): Promise<string | null> {
  try {
    const { shrinkDataUri } = await import("./image");
    let uri = dataUri;
    for (const [dim, q] of [[1280, 0.66], [1024, 0.6], [820, 0.55], [640, 0.5]] as const) {
      uri = await shrinkDataUri(uri, dim, q);
      if (uri.length <= SAFE_THUMB_BYTES) return uri;
    }
    return null;
  } catch { return null; }
}
function boundThumb(id: string) {
  const row = rows.get(id);
  if (!row || (row.dataUri?.length ?? 0) <= SAFE_THUMB_BYTES || !/^data:image\//i.test(row.dataUri)) return;
  void shrinkToFit(row.dataUri).then((fit) => {
    const cur = rows.get(id);
    if (!fit || !cur || cur.dataUri !== row.dataUri) return;
    const updated = { ...cur, dataUri: fit };
    commit([updated]);
    photoListener?.(updated);   // re-sync now that it fits
  });
}

/* --- original-quality upload queue ----------------------------------------
   Originals ride behind the record, never in front of it. One at a time so a
   phone on site isn't asked to hold several 4096px encodes in memory, each
   upload bounded by a timeout so a dead connection costs 2 minutes, not a
   report. A job that fails leaves its row exactly as filed — thumbnail only. */
const originalQueue: { id: string; file: File; caption?: string }[] = [];
let drainingOriginals = false;

async function idbPut(job: { id: string; file: File; caption?: string; attempts: number }) {
  const db = await idb();
  if (!db) return;
  try { db.transaction(IDB_STORE, "readwrite").objectStore(IDB_STORE).put(job); } catch { /* best-effort */ }
}
async function idbDelete(id: string) {
  const db = await idb();
  if (!db) return;
  try { db.transaction(IDB_STORE, "readwrite").objectStore(IDB_STORE).delete(id); } catch { /* best-effort */ }
}
async function idbAll(): Promise<{ id: string; file: File; caption?: string; attempts: number }[]> {
  const db = await idb();
  if (!db) return [];
  return getAll(db, IDB_STORE);
}

/* On app start: whatever the last session didn't finish, finish now. A row
   deleted since (or a job that keeps failing) is dropped rather than retried
   forever. Delayed a few seconds so boot isn't competing with first paint. */
if (typeof window !== "undefined") {
  setTimeout(async () => {
    await photoStoreReady;
    const leftovers = await idbAll();
    const db = await idb();
    for (const job of leftovers) {
      const row = rows.get(job.id) ?? (db ? await getOne<SitePhoto>(db, IDB_ROWS, job.id) : null);
      if (!row || row.fileId || job.attempts >= MAX_RESUME_ATTEMPTS) {
        void idbDelete(job.id);
        continue;
      }
      void idbPut({ ...job, attempts: job.attempts + 1 });
      originalQueue.push({ id: job.id, file: job.file, caption: job.caption });
    }
    if (originalQueue.length) {
      upState.total += originalQueue.length;
      upState.active = true;
      upEmit();
      void drainOriginals();
    }
  }, 4000);
}

const upState = { done: 0, total: 0, pct: 0, active: false };
const upSubs = new Set<() => void>();
const upEmit = () => upSubs.forEach((f) => f());

export function useOriginalUploads(): { done: number; total: number; pct: number; active: boolean } {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    upSubs.add(f);
    return () => { upSubs.delete(f); };
  }, []);
  return { ...upState };
}

function queueOriginal(id: string, file: File, caption?: string) {
  if (!upState.active && upState.done === upState.total) { upState.done = 0; upState.total = 0; }
  upState.total++;
  upState.active = true;
  upEmit();
  originalQueue.push({ id, file, caption });
  void idbPut({ id, file, caption, attempts: 0 });   // survives the tab
  void drainOriginals();
}

async function drainOriginals() {
  if (drainingOriginals) return;
  drainingOriginals = true;
  try {
    while (originalQueue.length) {
      const job = originalQueue.shift()!;
      upState.pct = 0;
      upEmit();
      try {
        const { full, width, height } = await encodePhoto(job.file);
        if (!full) void idbDelete(job.id);
        if (full) {
          const name = (job.caption || job.file.name || "photo").replace(/\.\w+$/, "") + ".jpg";
          const fileId = await uploadFileProgress(full, name, 120_000, (pct) => {
            if (pct - upState.pct >= 3 || pct === 100) { upState.pct = pct; upEmit(); }
          });
          if (fileId) {
            await photoActions.attachOriginal(job.id, fileId, width, height);
            void idbDelete(job.id);
          }
        }
      } catch { /* thumbnail-only is a photograph; a lost report is not */ }
      upState.done++;
      upState.pct = 0;
      upEmit();
    }
  } finally {
    drainingOriginals = false;
    upState.active = false;
    upState.pct = 0;
    upEmit();
  }
}

/* --- actions --------------------------------------------------------------- */
/** Patch one resident row; returns the updated row (or undefined if not resident). */
function patch(id: string, fn: (p: SitePhoto) => SitePhoto): SitePhoto | undefined {
  const cur = rows.get(id);
  if (!cur) return undefined;
  const updated = fn(cur);
  commit([updated]);
  return updated;
}

export const photoActions = {
  add(data: Omit<SitePhoto, "id" | "takenAt">): SitePhoto {
    const p: SitePhoto = { ...data, id: uid("ph-"), takenAt: new Date().toISOString() };
    loaded.add(p.projectId);            // its project is, by definition, in use
    commit([p]);
    photoListener?.(p);
    boundThumb(p.id);
    return p;
  },
  /** File one drafted shot — INSTANTLY. The row is written from the preview
      and synced; the camera original follows in the background queue. */
  async addShot(
    shot: { dataUri: string; file?: File; caption?: string },
    meta: Omit<SitePhoto, "id" | "takenAt" | "dataUri" | "fileId" | "fullWidth" | "fullHeight">,
  ): Promise<SitePhoto> {
    const row = photoActions.add({ ...meta, dataUri: shot.dataUri });
    if (shot.file) queueOriginal(row.id, shot.file, meta.caption);
    return row;
  },
  async attachOriginal(id: string, fileId: string, width: number, height: number): Promise<boolean> {
    let updated = patch(id, (p) => ({ ...p, fileId, fullWidth: width, fullHeight: height }));
    if (!updated) {
      // a resumed upload whose project is not open: the row is only on disk.
      // Patching memory alone used to drop the link — the original uploaded
      // and the photo never knew.
      const db = await idb();
      const row = db ? await getOne<SitePhoto>(db, IDB_ROWS, id) : null;
      if (row) { updated = { ...row, fileId, fullWidth: width, fullHeight: height }; commit([updated]); }
    }
    if (updated) photoListener?.(updated);
    return !!updated;
  },
  async addVideo(
    file: File,
    meta: Omit<SitePhoto, "id" | "takenAt" | "dataUri" | "fileId" | "media" | "durationSec" | "mimeType">,
  ): Promise<SitePhoto> {
    if (file.size > VIDEO_MAX_BYTES) {
      throw new Error(`That video is ${(file.size / 1024 / 1024).toFixed(0)} MB — the limit is ${VIDEO_MAX_BYTES / 1024 / 1024} MB. Trim it or record at a lower quality.`);
    }
    const { poster, durationSec } = await videoPoster(file);
    const fileId = await uploadFile(file, file.name);
    if (!fileId) throw new Error("The video couldn't be stored — check the connection and try again.");
    return photoActions.add({ ...meta, dataUri: poster, fileId, media: "video", durationSec, mimeType: file.type || "video/mp4" });
  },
  rename(id: string, caption: string): SitePhoto | undefined {
    const name = caption.trim();
    const updated = patch(id, (p) => ({ ...p, caption: name || undefined }));
    if (updated) photoListener?.(updated);
    return updated;
  },
  toggleFeatured(id: string): SitePhoto | undefined {
    const updated = patch(id, (p) => ({ ...p, featured: !p.featured }));
    if (updated) photoListener?.(updated);
    return updated;
  },
  setLinks(id: string, docIds: string[], ai = false): SitePhoto | undefined {
    const ids = [...new Set(docIds.filter(Boolean))];
    const updated = patch(id, (p) => ({ ...p, linkedDocIds: ids.length ? ids : undefined, linksAi: ai && ids.length ? true : undefined }));
    if (updated) photoListener?.(updated);
    return updated;
  },
  toggleLink(id: string, docId: string): SitePhoto | undefined {
    const p = rows.get(id);
    if (!p) return undefined;
    const has = (p.linkedDocIds ?? []).includes(docId);
    return photoActions.setLinks(id, has ? (p.linkedDocIds ?? []).filter((d) => d !== docId) : [...(p.linkedDocIds ?? []), docId]);
  },
  /** Delete a photo/render everywhere — removes the synced row. */
  remove(id: string) {
    if (!rows.has(id) && !meta.has(id)) return;
    commit([], [id]);
    photoDeleteListener?.(id);
  },
};
